import { coordLabel, type GameState, replay } from '@sea-wars/game-core';
import type { GameDetailResponse } from '@sea-wars/protocol';
import { hashSeed, SketchBoard } from '@sea-wars/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';

const AUTOPLAY_INTERVAL_MS = 900;

const END_REASON_LABEL: Record<GameDetailResponse['endReason'], string> = {
  victory: 'zatopieniem całej floty',
  resign: 'poddaniem się',
  timeout: 'przekroczeniem czasu',
  abandoned: 'porzuceniem partii',
  cancelled: 'anulowaniem przed pierwszym strzałem',
};

const RESULT_LABEL: Record<GameDetailResponse['moves'][number]['result'], string> = {
  miss: 'pudło',
  hit: 'trafienie',
  sunk: 'zatopiony',
};

function moveDescription(
  move: GameDetailResponse['moves'][number] | null,
  detail: GameDetailResponse,
  lastIndex: number,
  stepIndex: number,
): string {
  if (!move) return '';
  const shooter = detail.seats[move.seat]?.nickname ?? `gracz ${move.seat + 1}`;
  const target = coordLabel(move.x, move.y);
  return `Ruch ${stepIndex}/${lastIndex}: ${shooter} strzela w ${target} — ${RESULT_LABEL[move.result]}.`;
}

export function ReplayPage() {
  const { gameId } = useParams();
  const [detail, setDetail] = useState<GameDetailResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!gameId) return;
    api
      .gameDetail(gameId)
      .then(setDetail)
      .catch((error) => {
        if (error instanceof ApiError && error.status === 404) setNotFound(true);
      });
  }, [gameId]);

  const timeline = useMemo<GameState[] | null>(() => {
    if (!detail) return null;
    return replay(detail.ruleset, detail.fleets, detail.moves);
  }, [detail]);

  const lastIndex = (timeline?.length ?? 1) - 1;

  // Zatrzymujemy się na końcu zamiast zawijać — odtwarzacz ma pokazać wynik, nie pętlić go.
  useEffect(() => {
    if (!playing || !timeline) return;
    if (stepIndex >= lastIndex) {
      setPlaying(false);
      return;
    }
    const timer = setTimeout(
      () => setStepIndex((i) => Math.min(i + 1, lastIndex)),
      AUTOPLAY_INTERVAL_MS,
    );
    return () => clearTimeout(timer);
  }, [playing, stepIndex, lastIndex, timeline]);

  if (notFound) {
    return (
      <div className="card">
        <h2 className="hand">Nie ma takiego replayu</h2>
        <p className="app__note">Partia nie istnieje albo jeszcze się nie skończyła.</p>
        <Link to="/" className="btn btn--primary">
          Wróć do lobby
        </Link>
      </div>
    );
  }

  if (!detail || !timeline) {
    return <p className="app__status">Wczytywanie partii…</p>;
  }

  const state = timeline[stepIndex] ?? timeline[0];
  if (!state) return null;
  const move = stepIndex > 0 ? (detail.moves[stepIndex - 1] ?? null) : null;
  const seed = hashSeed(detail.id);
  const winnerNickname = detail.winner === null ? null : detail.seats[detail.winner]?.nickname;

  return (
    <>
      <div className="card game__header">
        <div>
          <h2 className="hand">
            {`${detail.seats[0]?.nickname ?? 'Gracz 1'} kontra ${detail.seats[1]?.nickname ?? 'Gracz 2'}`}
          </h2>
          <p className="app__note">
            {winnerNickname
              ? `Wygrał ${winnerNickname} — ${END_REASON_LABEL[detail.endReason]}.`
              : `Partia zakończona — ${END_REASON_LABEL[detail.endReason]}.`}
          </p>
        </div>
        <Link to="/" className="btn btn--ghost">
          Lobby
        </Link>
      </div>

      <p className="app__status hand" role="status" aria-live="polite">
        {stepIndex === 0
          ? 'Rozstawienie floty, przed pierwszym strzałem.'
          : moveDescription(move, detail, lastIndex, stepIndex)}
      </p>

      <div className="app__boards">
        {([0, 1] as const).map((seat) => (
          <section key={seat} className="board-card" aria-labelledby={`replay-board-${seat}`}>
            <h2 id={`replay-board-${seat}`} className="hand">
              {detail.seats[seat]?.nickname ?? `Gracz ${seat + 1}`}
            </h2>
            <SketchBoard
              size={detail.ruleset.boardSize}
              seed={seed + seat}
              fleet={state.boards[seat].fleet}
              shots={state.boards[seat].shots}
              ariaLabel={`Plansza gracza ${detail.seats[seat]?.nickname ?? seat + 1}`}
            />
          </section>
        ))}
      </div>

      <div className="app__controls replay__controls">
        <button
          type="button"
          className="btn"
          onClick={() => {
            setPlaying(false);
            setStepIndex(0);
          }}
          disabled={stepIndex === 0}
        >
          ⏮ Początek
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setPlaying(false);
            setStepIndex((i) => Math.max(0, i - 1));
          }}
          disabled={stepIndex === 0}
        >
          ◀ Krok wstecz
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setPlaying((p) => !p)}
          disabled={stepIndex >= lastIndex && !playing}
        >
          {playing ? '⏸ Pauza' : '▶ Odtwórz'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setPlaying(false);
            setStepIndex((i) => Math.min(lastIndex, i + 1));
          }}
          disabled={stepIndex >= lastIndex}
        >
          Krok dalej ▶
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setPlaying(false);
            setStepIndex(lastIndex);
          }}
          disabled={stepIndex >= lastIndex}
        >
          Koniec ⏭
        </button>
      </div>

      <input
        type="range"
        className="replay__slider"
        min={0}
        max={lastIndex}
        value={stepIndex}
        onChange={(event) => {
          setPlaying(false);
          setStepIndex(Number(event.target.value));
        }}
        aria-label="Krok replayu"
      />
    </>
  );
}
